"use client";

// MKT-#12: /sign-in must never show the sign-in form to an ALREADY-authenticated
// visitor. This is the safety net for the magic-link landing bug: a fresh redeem
// writes the cookie then hard-navigates to the dashboard, but if the
// mag-portal-access cookie isn't readable at that instant, middleware bounces the
// request to /sign-in?from=/dashboard (see AccessTokenCookieMirror's note).
// It also covers old bookmarks and superseded links tapped by a signed-in client.
//
// On mount this guard authoritatively checks the Cognito session; if signed in it
// makes sure the cookie is written (so /dashboard's middleware gate passes) and
// forwards to ?from= (default /dashboard), showing a "Signing you in..." overlay.
// Signed-out visitors see nothing extra - the form renders underneath untouched.

import { useEffect, useState } from "react";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { amplifyConfig } from "@/lib/amplify/config";
import { syncCookieFromSession } from "@/components/auth/AccessTokenCookieMirror";
import { safeReturnPath } from "@/lib/auth/return-path";

// Loop guard: if we forwarded to the destination and got bounced back here within
// this window, stop forwarding and show the form (prevents an infinite redirect
// if something downstream keeps rejecting the session).
const FORWARD_TS_KEY = "mag-signin-fwd-ts";
const FORWARD_LOOP_MS = 6000;

function recentlyForwarded(nowMs: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(FORWARD_TS_KEY) || 0);
    return last > 0 && nowMs - last < FORWARD_LOOP_MS;
  } catch {
    return false;
  }
}

export function SignInRedirectGuard({ signingYouIn }: { signingYouIn: string }) {
  // Starts false (matches SSR - no hydration mismatch). The async check below
  // flips it to true only right before forwarding an authenticated visitor, so
  // a signed-out visitor never sees the overlay and the form is never delayed.
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // A prior forward bounced straight back here -> stop, show the form.
    if (recentlyForwarded(Date.now())) {
      try {
        window.sessionStorage.removeItem(FORWARD_TS_KEY);
      } catch {
        // ignore
      }
      return;
    }

    // Ensure Amplify is configured regardless of the deferred-cluster timing.
    // /sign-in is an eager route, so this only guarantees the check below works
    // even if the dynamic AmplifyCluster chunk has not resolved yet. Idempotent.
    try {
      Amplify.configure(amplifyConfig);
    } catch {
      // already configured / no-op
    }

    const forward = async (): Promise<void> => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token || cancelled) return;
        // Signed in: make sure the cookie exists so the /dashboard middleware
        // gate passes on arrival, then hard-navigate to the intended target.
        await syncCookieFromSession();
        if (cancelled) return;
        const params = new URLSearchParams(window.location.search);
        const target = safeReturnPath(params.get("from"));
        try {
          window.sessionStorage.setItem(FORWARD_TS_KEY, String(Date.now()));
        } catch {
          // ignore
        }
        setRedirecting(true);
        window.location.assign(target);
      } catch {
        // Not signed in (or session unreadable) -> leave the form visible.
      }
    };
    void forward();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!redirecting) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="sign-in-redirecting"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white"
    >
      <p className="text-sm font-semibold tracking-wide text-brand-text">
        {signingYouIn}
      </p>
    </div>
  );
}
